import React, { useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { DsButton, DsAlert } from "../../../design-system";
import { assetService } from "../../../services/api";
import { IMPORT_EXPORT_KEYS, fieldLabel, inputSm } from "./constants";

function downloadWorkbook(rows, filename) {
  const list = Array.isArray(rows) && rows.length ? rows : [{ info: "No rows" }];
  const ws = XLSX.utils.json_to_sheet(list);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

function parseWorkbookFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(sheet, { defval: "" }));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsArrayBuffer(file);
  });
}

export default function AssetImportExportPanel({ mode = "import", canExport }) {
  const [key, setKey] = useState(IMPORT_EXPORT_KEYS[0].id);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const handleTemplate = async () => {
    setBusy(true);
    try {
      const res = await assetService.getTemplate(key);
      const tpl = res?.data || res || {};
      const cols = Array.isArray(tpl.columns) ? tpl.columns : [];
      const sample = Array.isArray(tpl.sample) ? tpl.sample : [];
      const rows =
        sample.length > 0
          ? sample
          : [Object.fromEntries(cols.map((c) => [c, ""]))];
      downloadWorkbook(rows, `${key}_template.xlsx`);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Template download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (!canExport) {
      toast.error("No export permission");
      return;
    }
    setBusy(true);
    try {
      const res = await assetService.exportData(key);
      const data = res?.data || res || [];
      const rows = Array.isArray(data) ? data : data.rows || [];
      downloadWorkbook(rows, `${key}_export.xlsx`);
      toast.success("Export ready");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setSummary(null);
    try {
      const rows = await parseWorkbookFile(file);
      const res = await assetService.importData(key, { rows });
      setSummary(res?.data || res || { ok: true });
      toast.success("Import processed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed");
      setSummary(err?.response?.data || { error: "Import failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">
          {mode === "export" ? "Asset Export" : "Asset Import"}
        </h1>
        <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
          Templates, bulk upload validation, and data export — via Admin Import/Export hub.
        </p>
      </div>

      <div className="ds-page-panel space-y-3 p-4">
        <label className="block max-w-md">
          <span className={fieldLabel}>Dataset</span>
          <select
            className={inputSm}
            value={key}
            onChange={(e) => setKey(e.target.value)}
          >
            {IMPORT_EXPORT_KEYS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap gap-2">
          <DsButton type="button" variant="outline" disabled={busy} onClick={handleTemplate}>
            Download template
          </DsButton>
          {mode === "export" ? (
            <DsButton type="button" disabled={busy || !canExport} onClick={handleExport}>
              Export Excel
            </DsButton>
          ) : (
            <label className="inline-flex">
              <DsButton type="button" disabled={busy} asChild={false}>
                <span className="relative">
                  Upload Excel
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="absolute inset-0 cursor-pointer opacity-0"
                    onChange={handleUpload}
                    disabled={busy}
                  />
                </span>
              </DsButton>
            </label>
          )}
        </div>

        {summary ? (
          <DsAlert tone={summary.errors?.length ? "warning" : "success"}>
            Created: {summary.created ?? 0}, Updated: {summary.updated ?? 0}
            {summary.errors?.length
              ? `, Errors: ${summary.errors.length}`
              : ""}
          </DsAlert>
        ) : null}
        {Array.isArray(summary?.errors) && summary.errors.length > 0 ? (
          <div className="max-h-40 overflow-auto rounded-lg border border-[hsl(var(--ds-border))] p-2 text-xs">
            {summary.errors.slice(0, 50).map((err, i) => (
              <div key={i}>
                Row {err.row}: {err.message}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
